// src/components/shifts/StaffAssignmentModal.js
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

const StaffAssignmentModal = ({ shift, onClose, onSuccess, onError }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('single'); // 'single' or 'bulk'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Single Assignment State - Staff List
    const [staffList, setStaffList] = useState([]);
    const [loadingStaff, setLoadingStaff] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStaffIds, setSelectedStaffIds] = useState([]);
    const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);

    // Bulk Assignment State
    const [csvFile, setCSVFile] = useState(null);
    const [uploadReport, setUploadReport] = useState(null);
    const [showReport, setShowReport] = useState(false);

    const isCoreAdmin = user?.user_type === 'core_admin';

    // Fetch staff list for the shift's premises
    useEffect(() => {
        if (activeTab === 'single' && shift?.premises_id) {
            fetchStaffList();
        }
    }, [activeTab, shift]);

    const fetchStaffList = async () => {
        setLoadingStaff(true);
        setError(null);

        try {
            const pageSize = 100; // Backend max page size
            let page = 1;
            let totalPages = 1;
            const allStaff = [];

            do {
                const response = await api.get('/staff', {
                    params: {
                        premises_id: shift.premises_id,
                        limit: pageSize,
                        page,
                        status: 'active'
                    }
                });

                const payload = response.data?.data || {};
                const staffPage = payload.staff || [];
                const pagination = payload.pagination || {};

                allStaff.push(...staffPage);
                totalPages = Number(pagination.total_pages || 1);
                page += 1;
            } while (page <= totalPages);

            setStaffList(allStaff);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to load staff list';
            setError(errorMessage);
        } finally {
            setLoadingStaff(false);
        }
    };

    // Filter staff based on search query
    const filteredStaff = staffList.filter(staff => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            staff.name?.toLowerCase().includes(query) ||
            staff.staff_id?.toLowerCase().includes(query) ||
            staff.email?.toLowerCase().includes(query) ||
            staff.phone_number?.toLowerCase().includes(query)
        );
    });

    // Handle individual checkbox toggle
    const handleStaffToggle = (staffId) => {
        setSelectedStaffIds(prev => {
            if (prev.includes(staffId)) {
                return prev.filter(id => id !== staffId);
            } else {
                return [...prev, staffId];
            }
        });
    };

    // Handle Select All toggle
    const handleSelectAll = () => {
        if (selectedStaffIds.length === filteredStaff.length) {
            setSelectedStaffIds([]);
        } else {
            setSelectedStaffIds(filteredStaff.map(staff => staff.id));
        }
    };

    // Handle multiple staff assignment
    const handleMultipleAssignment = async (e) => {
        e.preventDefault();

        if (selectedStaffIds.length === 0) {
            setError('Please select at least one staff member');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await api.post('/shift-assignments/multiple', {
                shift_id: shift.id,
                staff_ids: selectedStaffIds,
                effective_from: effectiveFrom
            });

            const message = response.data.message || 'Staff assigned successfully';
            setSuccess(message);
            setSelectedStaffIds([]);

            // Notify parent and refresh after a brief moment
            setTimeout(() => {
                onSuccess(message);
            }, 1500);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to assign staff';
            setError(errorMessage);
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle CSV file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'text/csv') {
            setCSVFile(file);
            setError(null);
        } else {
            setError('Please select a valid CSV file');
            setCSVFile(null);
        }
    };

    // Handle bulk upload
    const handleBulkUpload = async (e) => {
        e.preventDefault();

        if (!csvFile) {
            const errorMessage = 'Please select a CSV file';
            setError(errorMessage);
            if (onError) {
                onError(errorMessage);
            }
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        const formData = new FormData();
        formData.append('csv_file', csvFile);
        formData.append('shift_id', shift.id);

        try {
            const response = await api.post('/shift-assignments/bulk', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log('Bulk upload API response:', response.data);

            // Handle nested data structure from API
            // API returns: { status, message, data: { report: {...} }, timestamp, server_time }
            const report = response.data.data?.report || response.data.report || response.data;

            console.log('Extracted report:', report);

            setUploadReport(report);
            setShowReport(true);

            // Check if report has summary
            if (report && report.summary) {
                const message = `Upload complete: ${report.summary.success} successful, ${report.summary.failed} failed`;
                setSuccess(message);

                // Notify parent and refresh
                setTimeout(() => {
                    onSuccess(message);
                }, 500);
            } else {
                // Fallback if summary doesn't exist
                const message = response.data.message || 'Upload completed successfully';
                setSuccess(message);

                setTimeout(() => {
                    onSuccess(message);
                }, 500);
            }

            setCSVFile(null);

            // Reset file input
            document.getElementById('csv-file-input').value = '';
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to upload CSV';
            setError(errorMessage);
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // Download CSV template
    const downloadTemplate = () => {
        const headers = isCoreAdmin
            ? ['staff_id_or_phone', 'dial_code', 'effective_from']
            : ['staff_id_or_phone', 'effective_from'];

        const exampleRow = isCoreAdmin
            ? ['STF001 or 8012345678', '+234', '2025-11-11']
            : ['STF001 or 8012345678', '2025-11-11'];

        const csvContent = [
            headers.join(','),
            exampleRow.join(',')
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `staff_assignment_template_${shift.shift_name.replace(/\s+/g, '_')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    // Download upload report
    const downloadReport = () => {
        if (!uploadReport) return;

        const headers = ['Status', 'Staff Identifier', 'Dial Code', 'Reason'];
        const rows = uploadReport.details.map(detail => [
            detail.status,
            detail.staff_identifier,
            detail.dial_code || '',
            detail.reason || 'Success'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `assignment_report_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="modal-overlay">
            <div className="modal staff-assignment-modal">
                <div className="modal-header">
                    <h3>Assign Staff to {shift.shift_name}</h3>
                    <button className="close-btn" onClick={onClose} type="button">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    {/* Tab Navigation */}
                    <div className="tab-navigation">
                        <button
                            className={`tab-btn ${activeTab === 'single' ? 'active' : ''}`}
                            onClick={() => setActiveTab('single')}
                        >
                            <i className="fas fa-user"></i>
                            Single Assignment
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'bulk' ? 'active' : ''}`}
                            onClick={() => setActiveTab('bulk')}
                        >
                            <i className="fas fa-users"></i>
                            Bulk Upload
                        </button>
                    </div>

                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="success-message">
                            <i className="fas fa-check-circle"></i>
                            {success}
                        </div>
                    )}

                    {/* Single Assignment Tab */}
                    {activeTab === 'single' && (
                        <form onSubmit={handleMultipleAssignment} className="assignment-form">
                            {/* Effective From */}
                            <div className="form-group">
                                <label>Effective From *</label>
                                <input
                                    type="date"
                                    className="form-input"
                                    value={effectiveFrom}
                                    onChange={(e) => setEffectiveFrom(e.target.value)}
                                    required
                                />
                                <p className="form-help">
                                    The date from which this shift assignment becomes active
                                </p>
                            </div>

                            {/* Search Box */}
                            <div className="form-group">
                                <label>
                                    <i className="fas fa-search"></i> Search Staff
                                </label>
                                <input
                                    type="text"
                                    className="form-input search-input"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by name, staff ID, email, or phone..."
                                />
                            </div>

                            {/* Staff List */}
                            <div className="staff-list-container">
                                {loadingStaff ? (
                                    <div className="loading-spinner">
                                        <i className="fas fa-spinner fa-spin"></i>
                                        <p>Loading staff...</p>
                                    </div>
                                ) : filteredStaff.length === 0 ? (
                                    <div className="no-staff-message">
                                        <i className="fas fa-users-slash"></i>
                                        <p>{searchQuery ? 'No staff found matching your search' : 'No staff available in this premises'}</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Select All Checkbox */}
                                        <div className="select-all-section">
                                            <label className="checkbox-label select-all-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedStaffIds.length === filteredStaff.length && filteredStaff.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="checkbox-input"
                                                />
                                                <span className="checkbox-text">
                                                    <strong>Select All Staff ({filteredStaff.length})</strong>
                                                </span>
                                            </label>
                                            {selectedStaffIds.length > 0 && (
                                                <span className="selected-count">
                                                    {selectedStaffIds.length} staff selected
                                                </span>
                                            )}
                                        </div>

                                        {/* Staff List */}
                                        <div className="staff-list">
                                            {filteredStaff.map(staff => (
                                                <div key={staff.id} className="staff-item">
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedStaffIds.includes(staff.id)}
                                                            onChange={() => handleStaffToggle(staff.id)}
                                                            className="checkbox-input"
                                                        />
                                                        <div className="staff-info">
                                                            <div className="staff-name">
                                                                <i className="fas fa-user"></i>
                                                                {staff.name}
                                                            </div>
                                                            <div className="staff-details">
                                                                <span className="staff-id">
                                                                    <i className="fas fa-id-badge"></i>
                                                                    {staff.staff_id}
                                                                </span>
                                                                {staff.email && (
                                                                    <span className="staff-email">
                                                                        <i className="fas fa-envelope"></i>
                                                                        {staff.email}
                                                                    </span>
                                                                )}
                                                                {staff.designation && (
                                                                    <span className="staff-designation">
                                                                        <i className="fas fa-briefcase"></i>
                                                                        {staff.designation}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading || selectedStaffIds.length === 0}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Assigning...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-user-plus"></i>
                                            Assign {selectedStaffIds.length > 0 ? `${selectedStaffIds.length} Staff` : 'Staff'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Bulk Upload Tab */}
                    {activeTab === 'bulk' && (
                        <div className="bulk-upload-section">
                            <div className="template-section">
                                <h4><i className="fas fa-download"></i> Download Template</h4>
                                <p>Download the CSV template to assign multiple staff members at once</p>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={downloadTemplate}
                                >
                                    <i className="fas fa-file-csv"></i>
                                    Download CSV Template
                                </button>
                                <div className="template-info">
                                    <h5>CSV Format:</h5>
                                    <ul>
                                        {isCoreAdmin ? (
                                            <>
                                                <li><strong>Column 1:</strong> Staff ID or Phone Number (without dial code)</li>
                                                <li><strong>Column 2:</strong> Dial Code (e.g., +234, +1)</li>
                                                <li><strong>Column 3:</strong> Effective From (YYYY-MM-DD)</li>
                                            </>
                                        ) : (
                                            <>
                                                <li><strong>Column 1:</strong> Staff ID or Phone Number (without dial code)</li>
                                                <li><strong>Column 2:</strong> Effective From (YYYY-MM-DD)</li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            <div className="upload-section">
                                <h4><i className="fas fa-upload"></i> Upload CSV</h4>
                                <form onSubmit={handleBulkUpload}>
                                    <div className="file-input-wrapper">
                                        <input
                                            type="file"
                                            id="csv-file-input"
                                            accept=".csv"
                                            onChange={handleFileChange}
                                            className="file-input"
                                        />
                                        <label htmlFor="csv-file-input" className="file-label">
                                            <i className="fas fa-cloud-upload-alt"></i>
                                            {csvFile ? csvFile.name : 'Choose CSV File'}
                                        </label>
                                    </div>

                                    <div className="form-actions">
                                        <button type="submit" className="btn btn-primary" disabled={loading || !csvFile}>
                                            {loading ? (
                                                <>
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-upload"></i>
                                                    Upload & Assign
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Upload Report */}
                            {showReport && uploadReport && uploadReport.summary && (
                                <div className="upload-report">
                                    <div className="report-header">
                                        <h4><i className="fas fa-chart-bar"></i> Upload Report</h4>
                                        {uploadReport.details && uploadReport.details.length > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-secondary"
                                                onClick={downloadReport}
                                            >
                                                <i className="fas fa-download"></i>
                                                Download Report
                                            </button>
                                        )}
                                    </div>

                                    <div className="report-summary">
                                        <div className="summary-card success">
                                            <i className="fas fa-check-circle"></i>
                                            <div>
                                                <h5>{uploadReport.summary.success || 0}</h5>
                                                <p>Successful</p>
                                            </div>
                                        </div>
                                        <div className="summary-card failed">
                                            <i className="fas fa-times-circle"></i>
                                            <div>
                                                <h5>{uploadReport.summary.failed || 0}</h5>
                                                <p>Failed</p>
                                            </div>
                                        </div>
                                        <div className="summary-card total">
                                            <i className="fas fa-list"></i>
                                            <div>
                                                <h5>{uploadReport.summary.total || 0}</h5>
                                                <p>Total</p>
                                            </div>
                                        </div>
                                    </div>

                                    {uploadReport.details && uploadReport.details.length > 0 && (
                                        <div className="report-details">
                                            <h5>Details</h5>
                                            <div className="details-table-wrapper">
                                                <table className="details-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Status</th>
                                                            <th>Staff Identifier</th>
                                                            {isCoreAdmin && <th>Dial Code</th>}
                                                            <th>Reason</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {uploadReport.details.map((detail, index) => (
                                                            <tr key={index} className={detail.status === 'success' ? 'success-row' : 'error-row'}>
                                                                <td>
                                                                    <span className={`status-badge ${detail.status}`}>
                                                                        {detail.status === 'success' ? (
                                                                            <><i className="fas fa-check"></i> Success</>
                                                                        ) : (
                                                                            <><i className="fas fa-times"></i> Failed</>
                                                                        )}
                                                                    </span>
                                                                </td>
                                                                <td>{detail.staff_identifier}</td>
                                                                {isCoreAdmin && <td>{detail.dial_code || '-'}</td>}
                                                                <td>{detail.reason || 'Successfully assigned'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>

            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: var(--spacing-md);
                }

                .staff-assignment-modal {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-lg);
                    width: 100%;
                    max-width: 800px;
                    max-height: 90vh;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-xl);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                    background: var(--light-color);
                }

                .modal-header h3 {
                    margin: 0;
                    color: var(--text-dark);
                    font-size: 1.3rem;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 1.2rem;
                    cursor: pointer;
                    color: var(--text-light);
                    padding: var(--spacing-xs);
                    border-radius: var(--radius-sm);
                    transition: all 0.2s ease;
                }

                .close-btn:hover {
                    color: var(--text-dark);
                    background: rgba(0, 0, 0, 0.05);
                }

                .modal-body {
                    padding: var(--spacing-xl);
                    overflow-y: auto;
                    flex: 1;
                }

                .tab-navigation {
                    display: flex;
                    gap: var(--spacing-sm);
                    margin-bottom: var(--spacing-xl);
                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                }

                .tab-btn {
                    padding: var(--spacing-md) var(--spacing-lg);
                    background: none;
                    border: none;
                    border-bottom: 3px solid transparent;
                    cursor: pointer;
                    font-size: 0.95rem;
                    color: var(--text-light);
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .tab-btn:hover {
                    color: var(--admin-primary);
                }

                .tab-btn.active {
                    color: var(--admin-primary);
                    border-bottom-color: var(--admin-primary);
                }

                .error-message {
                    background: rgba(239, 68, 68, 0.1);
                    color: var(--error);
                    padding: var(--spacing-md);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--spacing-lg);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .success-message {
                    background: rgba(34, 197, 94, 0.1);
                    color: #16a34a;
                    padding: var(--spacing-md);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--spacing-lg);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .assignment-form {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-lg);
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-sm);
                }

                .form-group label {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .form-input {
                    padding: var(--spacing-md);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    transition: border-color 0.2s ease;
                }

                .form-input:focus {
                    outline: none;
                    border-color: var(--admin-primary);
                    box-shadow: 0 0 0 3px rgba(var(--admin-primary-rgb), 0.1);
                }

                .form-help {
                    font-size: 0.8rem;
                    color: var(--text-light);
                    margin: 0;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: var(--spacing-sm);
                    margin-top: var(--spacing-md);
                }

                .search-input {
                    font-size: 0.95rem;
                }

                .search-input::placeholder {
                    color: var(--text-light);
                }

                .staff-list-container {
                    margin-top: var(--spacing-lg);
                    border: 2px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-lg);
                    overflow: hidden;
                    max-height: 400px;
                    display: flex;
                    flex-direction: column;
                }

                .loading-spinner {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: var(--spacing-xl);
                    color: var(--admin-primary);
                    gap: var(--spacing-sm);
                }

                .loading-spinner i {
                    font-size: 2rem;
                }

                .no-staff-message {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: var(--spacing-xl);
                    color: var(--text-light);
                    gap: var(--spacing-sm);
                }

                .no-staff-message i {
                    font-size: 2.5rem;
                }

                .select-all-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-md) var(--spacing-lg);
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    border-bottom: 2px solid #bae6fd;
                }

                .select-all-label {
                    margin: 0 !important;
                }

                .selected-count {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: var(--admin-primary);
                    background: white;
                    padding: 0.3rem 0.75rem;
                    border-radius: var(--radius-full);
                }

                .staff-list {
                    overflow-y: auto;
                    flex: 1;
                }

                .staff-item {
                    padding: var(--spacing-md) var(--spacing-lg);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                    transition: background 0.2s ease;
                }

                .staff-item:hover {
                    background: rgba(var(--admin-primary-rgb), 0.03);
                }

                .staff-item:last-child {
                    border-bottom: none;
                }

                .checkbox-label {
                    display: flex;
                    align-items: flex-start;
                    gap: var(--spacing-md);
                    cursor: pointer;
                    margin: 0;
                }

                .checkbox-input {
                    width: 20px;
                    height: 20px;
                    cursor: pointer;
                    accent-color: var(--admin-primary);
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                .checkbox-text {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-weight: 600;
                    color: var(--text-dark);
                }

                .staff-info {
                    flex: 1;
                }

                .staff-name {
                    font-weight: 600;
                    color: var(--text-dark);
                    margin-bottom: 0.3rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.95rem;
                }

                .staff-name i {
                    color: var(--admin-primary);
                    font-size: 0.9rem;
                }

                .staff-details {
                    display: flex;
                    flex-wrap: wrap;
                    gap: var(--spacing-md);
                    font-size: 0.85rem;
                }

                .staff-details span {
                    display: flex;
                    align-items: center;
                    gap: 0.3rem;
                    color: var(--text-light);
                }

                .staff-details i {
                    font-size: 0.8rem;
                    color: var(--admin-primary);
                }

                .staff-id {
                    font-weight: 600;
                    color: var(--admin-primary);
                }

                .bulk-upload-section {
                    display: flex;
                    flex-direction: column;
                    gap: var(--spacing-xl);
                }

                .template-section,
                .upload-section {
                    padding: var(--spacing-lg);
                    border: 2px dashed rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-lg);
                }

                .template-section h4,
                .upload-section h4 {
                    margin: 0 0 var(--spacing-sm) 0;
                    color: var(--text-dark);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .template-section p,
                .upload-section p {
                    color: var(--text-light);
                    margin-bottom: var(--spacing-md);
                }

                .template-info {
                    margin-top: var(--spacing-lg);
                    padding: var(--spacing-md);
                    background: rgba(var(--admin-primary-rgb), 0.05);
                    border-radius: var(--radius-md);
                }

                .template-info h5 {
                    margin: 0 0 var(--spacing-sm) 0;
                    color: var(--text-dark);
                    font-size: 0.9rem;
                }

                .template-info ul {
                    margin: 0;
                    padding-left: var(--spacing-lg);
                }

                .template-info li {
                    font-size: 0.85rem;
                    color: var(--text-light);
                    margin-bottom: var(--spacing-xs);
                }

                .file-input-wrapper {
                    margin-bottom: var(--spacing-md);
                }

                .file-input {
                    display: none;
                }

                .file-label {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-sm);
                    padding: var(--spacing-xl);
                    border: 2px dashed var(--admin-primary);
                    border-radius: var(--radius-lg);
                    background: rgba(var(--admin-primary-rgb), 0.05);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 0.95rem;
                    color: var(--admin-primary);
                }

                .file-label:hover {
                    background: rgba(var(--admin-primary-rgb), 0.1);
                }

                .upload-report {
                    padding: var(--spacing-lg);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-lg);
                    background: var(--light-color);
                }

                .report-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: var(--spacing-lg);
                }

                .report-header h4 {
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .report-summary {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: var(--spacing-md);
                    margin-bottom: var(--spacing-lg);
                }

                .summary-card {
                    padding: var(--spacing-lg);
                    border-radius: var(--radius-md);
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-md);
                }

                .summary-card.success {
                    background: rgba(34, 197, 94, 0.1);
                    color: #16a34a;
                }

                .summary-card.failed {
                    background: rgba(239, 68, 68, 0.1);
                    color: var(--error);
                }

                .summary-card.total {
                    background: rgba(var(--admin-primary-rgb), 0.1);
                    color: var(--admin-primary);
                }

                .summary-card i {
                    font-size: 2rem;
                }

                .summary-card h5 {
                    margin: 0;
                    font-size: 1.8rem;
                }

                .summary-card p {
                    margin: 0;
                    font-size: 0.9rem;
                }

                .report-details h5 {
                    margin: 0 0 var(--spacing-md) 0;
                }

                .details-table-wrapper {
                    overflow-x: auto;
                }

                .details-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .details-table th,
                .details-table td {
                    padding: var(--spacing-sm);
                    text-align: left;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                }

                .details-table th {
                    background: rgba(0, 0, 0, 0.05);
                    font-weight: 600;
                    font-size: 0.85rem;
                }

                .details-table td {
                    font-size: 0.85rem;
                }

                .success-row {
                    background: rgba(34, 197, 94, 0.05);
                }

                .error-row {
                    background: rgba(239, 68, 68, 0.05);
                }

                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: var(--spacing-xs);
                    padding: var(--spacing-xs) var(--spacing-sm);
                    border-radius: var(--radius-sm);
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .status-badge.success {
                    background: #16a34a;
                    color: white;
                }

                .status-badge.failed {
                    background: var(--error);
                    color: white;
                }

                .modal-footer {
                    padding: var(--spacing-lg);
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    display: flex;
                    justify-content: flex-end;
                }

                .btn {
                    padding: var(--spacing-sm) var(--spacing-lg);
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: 0.9rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background: var(--admin-primary);
                    color: white;
                }

                .btn-primary:hover:not(:disabled) {
                    background: var(--admin-secondary);
                    transform: translateY(-1px);
                }

                .btn-secondary {
                    background: var(--white);
                    color: var(--text-dark);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f8f9fa;
                    border-color: var(--admin-primary);
                }

                .btn-sm {
                    padding: var(--spacing-xs) var(--spacing-sm);
                    font-size: 0.8rem;
                }

                @media (max-width: 768px) {
                    .staff-assignment-modal {
                        max-width: 100%;
                        max-height: 100vh;
                    }

                    .report-summary {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default StaffAssignmentModal;
